// The single description of what a feedback questionnaire *is*.
//
// The admin builder, the public form, the submit endpoint and the Excel export
// all read their rules from here, so a question type can never mean one thing
// in the builder and another at submit time. Nothing in this module touches
// React or Prisma — it is plain data + zod, importable from either side.

import { z } from "zod";
import { DEFAULT_LOCALE, LOCALES, type Locale } from "./i18n";

/* -------------------------------------------------------------------------- */
/*  Localized strings                                                         */
/* -------------------------------------------------------------------------- */

/** Every respondent-facing string exists in all three languages. */
export type Localized = Record<Locale, string>;

export const localizedSchema = z.object({
	ar: z.string().max(2000).default(""),
	fr: z.string().max(2000).default(""),
	en: z.string().max(2000).default(""),
});

export function emptyLocalized(): Localized {
	return { ar: "", fr: "", en: "" };
}

/**
 * Reads a localized string, falling back to any language that *is* filled in.
 *
 * A half-translated form must still be answerable: showing an empty question
 * would be worse than showing it in Arabic to a French reader, so the fallback
 * order is "asked language → Arabic (source of truth) → whatever exists".
 */
export function localized(
	value: Localized | undefined,
	locale: Locale,
): string {
	if (!value) return "";
	const asked = value[locale]?.trim();
	if (asked) return asked;
	const fallback = value[DEFAULT_LOCALE]?.trim();
	if (fallback) return fallback;
	for (const code of LOCALES) {
		const other = value[code]?.trim();
		if (other) return other;
	}
	return "";
}

/* -------------------------------------------------------------------------- */
/*  Question types                                                            */
/* -------------------------------------------------------------------------- */

export const QUESTION_TYPES = [
	"short_text",
	"long_text",
	"single_choice",
	"multi_choice",
	"dropdown",
	"grid",
	"scale",
	"email",
	"phone",
	"number",
	"date",
] as const;

export type QuestionType = (typeof QUESTION_TYPES)[number];

/** Admin-facing names (the dashboard is Arabic-only). */
export const QUESTION_TYPE_LABELS: Record<QuestionType, string> = {
	short_text: "نص قصير",
	long_text: "نص طويل",
	single_choice: "اختيار واحد",
	multi_choice: "اختيارات متعددة",
	dropdown: "قائمة منسدلة",
	grid: "شبكة تقييم",
	scale: "سلّم تنقيط",
	email: "بريد إلكتروني",
	phone: "رقم هاتف",
	number: "رقم",
	date: "تاريخ",
};

export const QUESTION_TYPE_HINTS: Record<QuestionType, string> = {
	short_text: "سطر واحد — اسم، منصب، إجابة مختصرة.",
	long_text: "فقرة كاملة — رأي مفصّل أو اقتراح.",
	single_choice: "أزرار دائرية، إجابة واحدة فقط (نعم / لا).",
	multi_choice: "مربّعات اختيار، يمكن اختيار أكثر من إجابة.",
	dropdown: "قائمة منسدلة — مناسبة عند كثرة الخيارات.",
	grid: "جدول: سطر لكل معيار وعمود لكل تقدير (ممتاز… ضعيف).",
	scale: "تنقيط رقمي من حد أدنى إلى حد أقصى (01 → 10).",
	email: "بريد إلكتروني مع تحقّق من الصيغة.",
	phone: "رقم هاتف مع تحقّق من الصيغة.",
	number: "قيمة رقمية فقط.",
	date: "منتقي تاريخ.",
};

/** Types whose answer is picked from `options`. */
export const CHOICE_TYPES = [
	"single_choice",
	"multi_choice",
	"dropdown",
] as const;

export function isChoiceType(type: QuestionType) {
	return (CHOICE_TYPES as readonly string[]).includes(type);
}

/**
 * Types another question may branch on. Only single-answer pickers qualify:
 * "show this when they answered Oui" has no meaning against a free-text or a
 * grid answer.
 */
export function canDriveConditions(type: QuestionType) {
	return type === "single_choice" || type === "dropdown";
}

/* -------------------------------------------------------------------------- */
/*  Schemas                                                                   */
/* -------------------------------------------------------------------------- */

export const choiceSchema = z.object({
	id: z.string().min(1).max(64),
	label: localizedSchema,
});

export type Choice = z.infer<typeof choiceSchema>;

/**
 * Show this question only when `questionId` was answered with `optionId`.
 * The referenced question must appear *earlier* in the list — enforced by
 * `questionsSchema` below, so a form can never contain a visibility cycle.
 */
export const conditionSchema = z.object({
	questionId: z.string().min(1).max(64),
	optionId: z.string().min(1).max(64),
});

export type Condition = z.infer<typeof conditionSchema>;

export const questionSchema = z.object({
	id: z.string().min(1).max(64),
	type: z.enum(QUESTION_TYPES),
	label: localizedSchema,
	/** Optional line under the question — "À l'issue de cette formation…". */
	help: localizedSchema.optional(),
	placeholder: localizedSchema.optional(),
	required: z.boolean().default(false),
	options: z.array(choiceSchema).max(60).default([]),
	/** Grid only — the criteria down the left edge. */
	rows: z.array(choiceSchema).max(60).default([]),
	/** Grid only — the ratings across the top. */
	columns: z.array(choiceSchema).max(15).default([]),
	scaleMin: z.number().int().min(0).max(100).default(1),
	scaleMax: z.number().int().min(1).max(100).default(10),
	scaleMinLabel: localizedSchema.optional(),
	scaleMaxLabel: localizedSchema.optional(),
	condition: conditionSchema.nullish(),
});

export type Question = z.infer<typeof questionSchema>;

/**
 * The whole list, with the cross-question rules a single question can't check:
 * unique ids, and conditions that only ever point backwards at a question that
 * actually offers the option they name.
 */
export const questionsSchema = z
	.array(questionSchema)
	.max(120)
	.superRefine((questions, ctx) => {
		const seen = new Set<string>();
		for (const [index, question] of questions.entries()) {
			if (seen.has(question.id)) {
				ctx.addIssue({
					code: "custom",
					path: [index, "id"],
					message: `Duplicate question id "${question.id}"`,
				});
			}
			seen.add(question.id);

			const condition = question.condition;
			if (!condition) continue;

			const sourceIndex = questions.findIndex(
				(q) => q.id === condition.questionId,
			);
			if (sourceIndex === -1 || sourceIndex >= index) {
				ctx.addIssue({
					code: "custom",
					path: [index, "condition"],
					message: "A condition must point at an earlier question",
				});
				continue;
			}
			if (
				!questions[sourceIndex].options.some((o) => o.id === condition.optionId)
			) {
				ctx.addIssue({
					code: "custom",
					path: [index, "condition"],
					message: "The condition's option no longer exists",
				});
			}
		}
	});

/** Page copy — the parts of the form that aren't questions. */
export const questionnaireCopySchema = z.object({
	title: z.string().max(300).default(""),
	description: z.string().max(3000).default(""),
	/** Small line above the title, e.g. "Votre avis". */
	eyebrow: z.string().max(160).default(""),
	submitLabel: z.string().max(80).default(""),
	thankYouTitle: z.string().max(300).default(""),
	thankYouText: z.string().max(2000).default(""),
});

export type QuestionnaireCopy = z.infer<typeof questionnaireCopySchema>;

export const questionnaireContentSchema = z.object({
	ar: questionnaireCopySchema,
	fr: questionnaireCopySchema,
	en: questionnaireCopySchema,
});

export type QuestionnaireContent = z.infer<typeof questionnaireContentSchema>;

/* -------------------------------------------------------------------------- */
/*  Answers                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * `string` for every single-value type, `string[]` for multi_choice, and
 * `Record<rowId, columnId>` for a grid.
 */
export type AnswerValue = string | string[] | Record<string, string>;

export const answerValueSchema = z.union([
	z.string().max(5000),
	z.array(z.string().max(200)).max(60),
	z.record(z.string().max(64), z.string().max(64)),
]);

export const answersSchema = z.record(z.string().max(64), answerValueSchema);

export type Answers = Record<string, AnswerValue>;

/* -------------------------------------------------------------------------- */
/*  Visibility + validation                                                   */
/* -------------------------------------------------------------------------- */

/** A conditional question is hidden until its trigger option is the answer. */
export function isVisible(question: Question, answers: Answers): boolean {
	const condition = question.condition;
	if (!condition) return true;
	const source = answers[condition.questionId];
	if (typeof source === "string") return source === condition.optionId;
	if (Array.isArray(source)) return source.includes(condition.optionId);
	return false;
}

/**
 * The questions actually on screen for a given set of answers.
 *
 * Conditions may chain (Q7 shows when Q6 shows and was answered "yes"), so a
 * hidden question's own answer must not reveal a later one — visibility is
 * walked in order and a hidden question contributes nothing downstream.
 */
export function visibleQuestions(
	questions: Question[],
	answers: Answers,
): Question[] {
	const shown: Question[] = [];
	const live: Answers = {};
	for (const question of questions) {
		if (!isVisible(question, live)) continue;
		shown.push(question);
		if (question.id in answers) live[question.id] = answers[question.id];
	}
	return shown;
}

export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const PHONE_PATTERN = /^[0-9+][0-9\s+-]{7,19}$/;

/** Localized validation copy, so the same checks can speak all three languages. */
export interface ValidationStrings {
	required: string;
	email: string;
	phone: string;
	number: string;
	gridIncomplete: string;
}

export const VALIDATION_STRINGS: Record<Locale, ValidationStrings> = {
	ar: {
		required: "هذا السؤال إجباري",
		email: "بريد إلكتروني غير صالح",
		phone: "رقم هاتف غير صالح",
		number: "أدخل رقماً صالحاً",
		gridIncomplete: "يرجى تقييم كل البنود",
	},
	fr: {
		required: "Cette question est obligatoire",
		email: "Adresse e-mail invalide",
		phone: "Numéro de téléphone invalide",
		number: "Entrez un nombre valide",
		gridIncomplete: "Merci d’évaluer tous les critères",
	},
	en: {
		required: "This question is required",
		email: "Invalid email address",
		phone: "Invalid phone number",
		number: "Enter a valid number",
		gridIncomplete: "Please rate every item",
	},
};

/**
 * The choices a respondent can actually read.
 *
 * A row/column/option left blank in every language is unanswerable: it draws as
 * an empty line and means nothing. The renderer drops them, so the validator
 * must drop them too — otherwise a *required* grid with an unfilled row (which
 * is exactly what the starter template hands the admin) becomes permanently
 * unsubmittable, demanding a rating for a row nobody can see or interpret.
 */
export function usableChoices(choices: Choice[]): Choice[] {
	return choices.filter((choice) =>
		LOCALES.some((code) => choice.label[code]?.trim()),
	);
}

/** True when an answer holds nothing a human typed or picked. */
export function isBlank(value: AnswerValue | undefined): boolean {
	if (value === undefined || value === null) return true;
	if (typeof value === "string") return value.trim() === "";
	if (Array.isArray(value)) return value.length === 0;
	return Object.keys(value).length === 0;
}

/**
 * Coerces whatever arrived into the shape this question's type expects, and
 * drops values that don't name a real option/row/column any more (an answer to
 * a choice deleted since the page loaded, or a forged post).
 */
export function normalizeAnswer(
	question: Question,
	raw: AnswerValue | undefined,
): AnswerValue {
	switch (question.type) {
		case "multi_choice": {
			const ids = new Set(question.options.map((o) => o.id));
			const list = Array.isArray(raw)
				? raw
				: typeof raw === "string" && raw
					? [raw]
					: [];
			return list.filter((id) => ids.has(id));
		}
		case "single_choice":
		case "dropdown": {
			const ids = new Set(question.options.map((o) => o.id));
			const value = typeof raw === "string" ? raw : "";
			return ids.has(value) ? value : "";
		}
		case "grid": {
			const rows = new Set(question.rows.map((r) => r.id));
			const columns = new Set(question.columns.map((c) => c.id));
			const source =
				raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
			const out: Record<string, string> = {};
			for (const [rowId, columnId] of Object.entries(source)) {
				if (rows.has(rowId) && columns.has(columnId)) out[rowId] = columnId;
			}
			return out;
		}
		case "scale": {
			const value = typeof raw === "string" ? raw.trim() : "";
			const n = Number(value);
			if (!value || Number.isNaN(n)) return "";
			return n >= question.scaleMin && n <= question.scaleMax ? String(n) : "";
		}
		default:
			return typeof raw === "string" ? raw.trim() : "";
	}
}

/**
 * Validates a submission against the form, in the respondent's language.
 *
 * Runs on both sides on purpose: the client uses it to point at the first bad
 * field, and `questionnaires.submit` re-runs it because a posted body is never
 * trustworthy. Returns `errors` keyed by question id plus the `answers` that
 * should actually be stored — answers to questions the respondent never saw are
 * dropped rather than persisted, so a "Si oui" answer can't survive flipping
 * the parent back to "Non".
 */
export function validateAnswers(
	questions: Question[],
	answers: Answers,
	locale: Locale,
): { errors: Record<string, string>; answers: Answers } {
	const strings =
		VALIDATION_STRINGS[locale] ?? VALIDATION_STRINGS[DEFAULT_LOCALE];
	const errors: Record<string, string> = {};
	const kept: Answers = {};

	for (const question of visibleQuestions(questions, answers)) {
		const value = normalizeAnswer(question, answers[question.id]);

		if (isBlank(value)) {
			if (question.required) errors[question.id] = strings.required;
			continue;
		}

		kept[question.id] = value;

		if (typeof value === "string") {
			if (question.type === "email" && !EMAIL_PATTERN.test(value)) {
				errors[question.id] = strings.email;
			}
			if (question.type === "phone" && !PHONE_PATTERN.test(value)) {
				errors[question.id] = strings.phone;
			}
			if (question.type === "number" && Number.isNaN(Number(value))) {
				errors[question.id] = strings.number;
			}
		}

		// A partly-filled grid is the one case where "not blank" still isn't
		// answered: every criterion needs its own rating.
		if (
			question.type === "grid" &&
			question.required &&
			typeof value === "object" &&
			!Array.isArray(value) &&
			usableChoices(question.rows).some((row) => !value[row.id])
		) {
			errors[question.id] = strings.gridIncomplete;
		}
	}

	return { errors, answers: kept };
}

/* -------------------------------------------------------------------------- */
/*  Rendering answers back (responses table + Excel export)                    */
/* -------------------------------------------------------------------------- */

/** Reads one stored answer back as human text, in `locale`. */
export function formatAnswer(
	question: Question,
	value: AnswerValue | undefined,
	locale: Locale,
): string {
	if (isBlank(value)) return "";

	const labelOf = (choices: Choice[], id: string) => {
		const found = choices.find((c) => c.id === id);
		return found ? localized(found.label, locale) : id;
	};

	switch (question.type) {
		case "single_choice":
		case "dropdown":
			return labelOf(question.options, value as string);
		case "multi_choice":
			return (value as string[])
				.map((id) => labelOf(question.options, id))
				.join(" • ");
		case "grid":
			return question.rows
				.map((row) => {
					const columnId = (value as Record<string, string>)[row.id];
					if (!columnId) return null;
					return `${localized(row.label, locale)}: ${labelOf(question.columns, columnId)}`;
				})
				.filter(Boolean)
				.join(" | ");
		default:
			return String(value);
	}
}

/* -------------------------------------------------------------------------- */
/*  Factories — what "add a question" produces                                */
/* -------------------------------------------------------------------------- */

/**
 * Short, collision-safe id. Ids only need to be unique inside one form, and
 * they end up as JSON keys in every stored response, so they stay short and
 * readable rather than being full uuids.
 */
export function newId(prefix: string) {
	return `${prefix}_${Math.random().toString(36).slice(2, 8)}${Date.now().toString(36).slice(-3)}`;
}

export function newChoice(labels?: Partial<Localized>): Choice {
	return { id: newId("o"), label: { ...emptyLocalized(), ...labels } };
}

/** The five-point rating scale the printed feedback sheet uses. */
export const RATING_COLUMNS: Localized[] = [
	{ ar: "ممتاز", fr: "Excellent", en: "Excellent" },
	{ ar: "جيد جداً", fr: "Très bien", en: "Very good" },
	{ ar: "جيد", fr: "Bien", en: "Good" },
	{ ar: "متوسط", fr: "Moyen", en: "Average" },
	{ ar: "ضعيف", fr: "Faible", en: "Poor" },
];

/** Sensible starting content per type, so a new question is never a blank slab. */
export function newQuestion(type: QuestionType): Question {
	const base: Question = {
		id: newId("q"),
		type,
		label: emptyLocalized(),
		help: emptyLocalized(),
		placeholder: emptyLocalized(),
		required: false,
		options: [],
		rows: [],
		columns: [],
		scaleMin: 1,
		scaleMax: 10,
		condition: null,
	};

	if (isChoiceType(type)) {
		return {
			...base,
			options: [
				newChoice({ ar: "نعم", fr: "Oui", en: "Yes" }),
				newChoice({ ar: "لا", fr: "Non", en: "No" }),
			],
		};
	}

	if (type === "grid") {
		return {
			...base,
			rows: [newChoice(), newChoice(), newChoice()],
			columns: RATING_COLUMNS.map((labels) => newChoice(labels)),
		};
	}

	return base;
}

export function emptyContent(): QuestionnaireContent {
	const copy: QuestionnaireCopy = {
		title: "",
		description: "",
		eyebrow: "",
		submitLabel: "",
		thankYouTitle: "",
		thankYouText: "",
	};
	return { ar: { ...copy }, fr: { ...copy }, en: { ...copy } };
}

/** Parses the DB's `questions` JSON, tolerating rows written by an older shape. */
export function parseQuestions(value: unknown): Question[] {
	const parsed = questionsSchema.safeParse(value);
	if (parsed.success) return parsed.data;
	// Never fail the public page over one malformed question — keep the ones
	// that still parse so the rest of the form stays answerable.
	if (!Array.isArray(value)) return [];
	return value.flatMap((item) => {
		const one = questionSchema.safeParse(item);
		return one.success ? [one.data] : [];
	});
}

export function parseContent(value: unknown): QuestionnaireContent {
	const parsed = questionnaireContentSchema.safeParse(value);
	return parsed.success ? parsed.data : emptyContent();
}

/* -------------------------------------------------------------------------- */
/*  Public form chrome                                                        */
/* -------------------------------------------------------------------------- */

// Copy the respondent reads that isn't authored per training: the required
// marker, the fallbacks for an admin who left the title blank, the submit
// button, the error summary. It sits here rather than in i18n.tsx's UI_STRINGS
// because VALIDATION_STRINGS above is the other half of the same vocabulary —
// splitting one form's wording across two modules only invites drift.

export interface FormStrings {
	/** Fallbacks used when the admin hasn't written that piece of copy. */
	defaultTitle: string;
	defaultSubmit: string;
	defaultThankYouTitle: string;
	defaultThankYouText: string;
	requiredMark: string;
	requiredNote: string;
	optional: string;
	otherAnswers: string;
	identityHeading: string;
	identityName: string;
	identityEmail: string;
	identityPhone: string;
	errorSummary: string;
	submitting: string;
	closedTitle: string;
	closedText: string;
	emptyTitle: string;
	emptyText: string;
	backToTraining: string;
	scaleLow: string;
	scaleHigh: string;
	/** Precedes the edition number under the training name, e.g. "Édition 2". */
	edition: string;
}

export const FORM_STRINGS: Record<Locale, FormStrings> = {
	ar: {
		defaultTitle: "استمارة تقييم التكوين",
		defaultSubmit: "إرسال التقييم",
		defaultThankYouTitle: "شكراً لك!",
		defaultThankYouText:
			"تم تسجيل إجابتك بنجاح. رأيك يساعدنا على تحسين الدورات القادمة.",
		requiredMark: "*",
		requiredNote: "الأسئلة المعلّمة بـ * إجبارية.",
		optional: "اختياري",
		otherAnswers: "إجابات أخرى",
		identityHeading: "معلوماتك",
		identityName: "الاسم الكامل",
		identityEmail: "البريد الإلكتروني",
		identityPhone: "رقم الهاتف",
		errorSummary: "يرجى مراجعة الأسئلة المعلّمة بالأحمر.",
		submitting: "جارٍ الإرسال…",
		closedTitle: "الاستمارة مغلقة حالياً",
		closedText:
			"لم تعد هذه الاستمارة مفتوحة للإجابة. إن كنت تظن أن هذا خطأ، تواصل مع فريق الأكاديمية.",
		emptyTitle: "الاستمارة قيد التحضير",
		emptyText: "لم تُضَف أي أسئلة بعد. عد إلينا قريباً.",
		backToTraining: "العودة إلى صفحة الدورة",
		scaleLow: "الأدنى",
		scaleHigh: "الأعلى",
		edition: "الطبعة",
	},
	fr: {
		defaultTitle: "Questionnaire d’évaluation de la formation",
		defaultSubmit: "Envoyer mon évaluation",
		defaultThankYouTitle: "Merci !",
		defaultThankYouText:
			"Votre réponse a bien été enregistrée. Votre avis nous aide à améliorer les prochaines formations.",
		requiredMark: "*",
		requiredNote: "Les questions marquées d’un * sont obligatoires.",
		optional: "facultatif",
		otherAnswers: "Autres réponses",
		identityHeading: "Vos informations",
		identityName: "Nom complet",
		identityEmail: "Adresse e-mail",
		identityPhone: "Téléphone",
		errorSummary: "Merci de vérifier les questions signalées en rouge.",
		submitting: "Envoi en cours…",
		closedTitle: "Ce questionnaire est fermé",
		closedText:
			"Ce questionnaire n’accepte plus de réponses. Si vous pensez qu’il s’agit d’une erreur, contactez l’équipe de l’académie.",
		emptyTitle: "Questionnaire en préparation",
		emptyText: "Aucune question n’a encore été ajoutée. Revenez bientôt.",
		backToTraining: "Retour à la page de la formation",
		scaleLow: "Minimum",
		scaleHigh: "Maximum",
		edition: "Édition",
	},
	en: {
		defaultTitle: "Training feedback form",
		defaultSubmit: "Send my feedback",
		defaultThankYouTitle: "Thank you!",
		defaultThankYouText:
			"Your response has been recorded. Your feedback helps us improve the next sessions.",
		requiredMark: "*",
		requiredNote: "Questions marked with * are required.",
		optional: "optional",
		otherAnswers: "Other answers",
		identityHeading: "Your details",
		identityName: "Full name",
		identityEmail: "Email address",
		identityPhone: "Phone number",
		errorSummary: "Please review the questions highlighted in red.",
		submitting: "Sending…",
		closedTitle: "This form is closed",
		closedText:
			"This questionnaire is no longer accepting responses. If you think this is a mistake, get in touch with the academy team.",
		emptyTitle: "Form in preparation",
		emptyText: "No questions have been added yet. Please check back soon.",
		backToTraining: "Back to the training page",
		scaleLow: "Lowest",
		scaleHigh: "Highest",
		edition: "Edition",
	},
};

/* -------------------------------------------------------------------------- */
/*  Structural repair                                                          */
/* -------------------------------------------------------------------------- */

/**
 * Drops conditions that no longer make sense, so an edit can never leave a
 * question permanently invisible.
 *
 * A condition dies when its source is deleted, moved after the question that
 * depends on it, retyped to something that can't drive a branch, or loses the
 * option it was watching. The dependent question survives — it just becomes
 * unconditional, which is visible and fixable, unlike a question that silently
 * never renders. `questionsSchema` rejects all four cases, so running this
 * after any structural edit is what keeps the builder saveable.
 */
export function pruneConditions(questions: Question[]): Question[] {
	return questions.map((question, index) => {
		const condition = question.condition;
		if (!condition) return question;

		const sourceIndex = questions.findIndex(
			(q) => q.id === condition.questionId,
		);
		const source = sourceIndex === -1 ? undefined : questions[sourceIndex];
		const stillValid =
			source !== undefined &&
			sourceIndex < index &&
			canDriveConditions(source.type) &&
			source.options.some((o) => o.id === condition.optionId);

		return stillValid ? question : { ...question, condition: null };
	});
}

/** True when every condition still points backwards at an option that exists. */
export function conditionsAreValid(questions: Question[]): boolean {
	return pruneConditions(questions).every(
		(question, index) => question.condition === questions[index].condition,
	);
}

/* -------------------------------------------------------------------------- */
/*  Card layout                                                                */
/* -------------------------------------------------------------------------- */

/** One numbered card on the public form, plus the follow-ups nested in it. */
export interface QuestionGroup {
	/** The numbered question that owns the card. */
	lead: Question;
	/** Conditional questions shown inside the same card once their trigger is met. */
	followUps: Question[];
}

/**
 * Splits the flat question list into the cards the public form renders.
 *
 * A question with no condition always opens a new card. A conditional one joins
 * the card of whatever it branches off — which is its own condition source, or
 * that source's card when the source is itself a follow-up, so a chain of
 * "si oui → si oui" stays in one card instead of spawning empty ones.
 *
 * The point is stable numbering: question 05 stays 05 whether or not its
 * "Si oui, lesquelles ?" follow-up happens to be showing, which is how the
 * paper sheet reads and what a respondent half-way down the page expects.
 */
export function groupQuestions(questions: Question[]): QuestionGroup[] {
	const groups: QuestionGroup[] = [];
	const cardOf = new Map<string, QuestionGroup>();

	for (const question of questions) {
		const parent = question.condition
			? cardOf.get(question.condition.questionId)
			: undefined;
		if (parent) {
			parent.followUps.push(question);
			cardOf.set(question.id, parent);
			continue;
		}
		const group: QuestionGroup = { lead: question, followUps: [] };
		groups.push(group);
		cardOf.set(question.id, group);
	}

	return groups;
}

/* -------------------------------------------------------------------------- */
/*  Raw JSON editing                                                          */
/* -------------------------------------------------------------------------- */

/**
 * What the builder's JSON tab edits: the whole form definition, and nothing
 * else. The publish switch and the identity toggle stay out on purpose — this
 * document is the part you would copy from one training to another, and
 * carrying `isActive` along would silently publish the copy.
 */
export const questionnaireDocumentSchema = z.object({
	content: questionnaireContentSchema,
	questions: questionsSchema,
});

export type QuestionnaireDocument = z.infer<typeof questionnaireDocumentSchema>;

export type DocumentParseResult =
	| { ok: true; document: QuestionnaireDocument }
	| { ok: false; error: string };

/** Renders a zod path as `questions[2].options[0].label` for the error line. */
function formatPath(path: ReadonlyArray<PropertyKey>): string {
	let out = "";
	for (const segment of path) {
		if (typeof segment === "number") out += `[${segment}]`;
		else out += out ? `.${String(segment)}` : String(segment);
	}
	return out;
}

/**
 * Parses the JSON tab's text into a form definition.
 *
 * Deliberately strict: a questionnaire that parses as JSON but not as a valid
 * form would break the public page for real respondents, so a malformed
 * document is rejected here rather than saved and discovered later. The
 * message names the offending path, because "invalid" alone is useless against
 * a hundred-line document.
 */
export function parseQuestionnaireDocument(text: string): DocumentParseResult {
	let raw: unknown;
	try {
		raw = JSON.parse(text);
	} catch {
		return {
			ok: false,
			error: "صياغة JSON غير صالحة — تحقّق من الأقواس والفواصل.",
		};
	}

	if (raw === null || typeof raw !== "object" || Array.isArray(raw)) {
		return {
			ok: false,
			error: "الجذر يجب أن يكون كائناً يحتوي على content و questions.",
		};
	}

	const parsed = questionnaireDocumentSchema.safeParse(raw);
	if (parsed.success) return { ok: true, document: parsed.data };

	const lines = parsed.error.issues
		.slice(0, 4)
		.map((issue) => {
			const path = formatPath(issue.path);
			return path ? `${path}: ${issue.message}` : issue.message;
		})
		.join(" · ");
	const more = parsed.error.issues.length > 4 ? " …" : "";
	return { ok: false, error: `${lines}${more}` };
}

/** Serializes a form definition for the JSON tab. */
export function stringifyQuestionnaireDocument(
	document: QuestionnaireDocument,
): string {
	return JSON.stringify(document, null, 2);
}
